/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Grid, useMediaQuery } from '@coursera/cds-core';

import EnterpriseMobileSearchBarLegacyGridItem from 'bundles/composable-page-header-enterprise/components/EnterpriseMobileSearchBarLegacyGridItem';
import EnterpriseOrgScopedSearchBarGridItem from 'bundles/composable-page-header-enterprise/components/EnterpriseOrgScopedSearchBarGridItem';
import type { UseEnterpriseMyPageHeaderResult as MyEnterprisePageHeader } from 'bundles/composable-page-header-enterprise/contexts/EnterpriseMyPageHeaderContext';
import DefaultLoggedInEnterpriseMobileMenuConfig from 'bundles/composable-page-header-enterprise/module-renderers/DefaultLoggedInEnterpriseMobileMenuConfig';
import {
  getEnterpriseLogoLabel,
  getEnterpriseProgramLogos,
  getLinkForEnterpriseProgram,
} from 'bundles/composable-page-header-enterprise/utils/enterpriseLogoUtils';
import HelpDropdownGridItem from 'bundles/composable-page-header/components/HelpDropdownGridItem';
import MainNavigationWrapper from 'bundles/composable-page-header/components/MainNavigationWrapper';
import NotificationsLegacyGridItem from 'bundles/composable-page-header/components/grid-items/NotificationsLegacyGridItem';
import CourseraHomeLogo from 'bundles/composable-page-header/components/grid-items/home-logo/CourseraHomeLogo';
import HomeLogoGridItemModule from 'bundles/composable-page-header/components/grid-items/home-logo/HomeLogoGridItemModule';
import MobileMenuIconGridItem from 'bundles/composable-page-header/components/grid-items/mobile/MobileMenuIconGridItem';
import { MOBILE_HEADER_BREAKPOINT_MEDIA_QUERY } from 'bundles/composable-page-header/constants';
import useShouldShowHelpDropdown from 'bundles/composable-page-header/hooks/useShouldShowHelpDropdown';
import useUserAgent from 'bundles/page/hooks/useUserAgent';
import useUnifiedEnterpriseSearch from 'bundles/search-common/hooks/useUnifiedEnterpriseSearch';

const styles = {
  fullContainer: css`
    flex-wrap: nowrap;
  `,
  leftSide: css`
    flex-wrap: nowrap;
  `,
  rightSide: css`
    flex-wrap: nowrap;
  `,
};

type Props = {
  pageHeaderData: MyEnterprisePageHeader;
  hideSearch?: boolean;
  injectedSearch?: React.ReactNode;
};

export default function LoggedInEnterpriseMobileMainNav({ pageHeaderData, hideSearch, injectedSearch }: Props) {
  const {
    enrolledDegreePrograms,
    enrolledEnterprisePrograms,
    chosenDegree,
    chosenEnterpriseProgram,
    hasEnrolledPrograms,
    naptimeManagedThirdPartyOrganizations,
    hasCheatingIncidents,
    canViewEducatorAdminLink,
    currentPageProgram,
  } = pageHeaderData;

  const currentProgram = currentPageProgram ?? chosenEnterpriseProgram;
  const userAgent = useUserAgent();
  const isMobileView = useMediaQuery(MOBILE_HEADER_BREAKPOINT_MEDIA_QUERY, {
    defaultMatches: userAgent?.isMobileBrowser || userAgent?.isMobile,
  });
  const showUnifiedSearch = useUnifiedEnterpriseSearch();
  const shouldShowHelpDropdown = useShouldShowHelpDropdown();
  const unifiedSearchComponent = isMobileView ? (
    <EnterpriseOrgScopedSearchBarGridItem
      thirdPartyOrganizationSlug={currentProgram?.naptimeThirdPartyOrganization?.slug}
      enrolledEnterprisePrograms={enrolledEnterprisePrograms}
      chosenProgramSlug={currentProgram?.slug}
      chosenProgramId={currentProgram?.id}
    />
  ) : null;
  const searchComponent =
    injectedSearch ??
    (showUnifiedSearch ? (
      unifiedSearchComponent
    ) : (
      <EnterpriseMobileSearchBarLegacyGridItem
        currentProgram={currentProgram}
        enterprisePrograms={enrolledEnterprisePrograms}
      />
    ));

  const logo = currentProgram ? getEnterpriseProgramLogos(currentProgram) : [<CourseraHomeLogo key="1" />];

  const LogoComponent = (
    <HomeLogoGridItemModule
      href={currentProgram ? getLinkForEnterpriseProgram(currentProgram) : '/'}
      logos={logo}
      ariaLabel={currentProgram ? getEnterpriseLogoLabel(currentProgram) : undefined}
    />
  );

  return (
    <MainNavigationWrapper>
      <Grid item container css={styles.fullContainer}>
        <Grid item container alignItems="center" css={styles.leftSide}>
          <MobileMenuIconGridItem
            menuConfig={
              <DefaultLoggedInEnterpriseMobileMenuConfig
                currentProgram={currentProgram}
                enrolledDegreePrograms={enrolledDegreePrograms}
                enrolledEnterprisePrograms={enrolledEnterprisePrograms}
                chosenDegree={chosenDegree}
                chosenEnterpriseProgram={chosenEnterpriseProgram}
                hasEnrolledPrograms={!!hasEnrolledPrograms}
                naptimeManagedThirdPartyOrganizations={naptimeManagedThirdPartyOrganizations}
                hasCheatingIncidents={hasCheatingIncidents}
                canViewEducatorAdminLink={canViewEducatorAdminLink}
              />
            }
            homeLogo={LogoComponent}
          />
          {LogoComponent}
        </Grid>
        <Grid item container alignItems="center" justifyContent="flex-end" css={styles.rightSide}>
          {shouldShowHelpDropdown && <HelpDropdownGridItem disableChatSsr={true} />}
          <NotificationsLegacyGridItem />
          {!hideSearch && searchComponent}
        </Grid>
      </Grid>
    </MainNavigationWrapper>
  );
}
